import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, FileText, Check, X, Loader2, Eye, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  not_uploaded: 'bg-slate-100 text-slate-500 border-slate-200',
  uploaded: 'bg-blue-50 text-blue-700 border-blue-200',
  verified: 'bg-green-50 text-green-700 border-green-200',
};

const STATUS_LABELS = {
  not_uploaded: 'Not Uploaded',
  uploaded: 'Uploaded',
  verified: 'Verified',
};

export default function DocumentUpload({ 
  clientId, 
  docType, 
  label, 
  documentData,
  onUploadSuccess,
  onDeleteSuccess,
  onVerifySuccess,
  disabled = false 
}) {
  const [uploading, setUploading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const fileInputRef = useRef(null);
  
  const status = documentData?.status || 'not_uploaded';
  const fileName = documentData?.file_name || null;
  
  const getToken = () => localStorage.getItem('ratnatraya_token');

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/clients/${clientId}/documents/${docType}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      toast.success(`${label} uploaded successfully`);
      onUploadSuccess?.(docType, response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${label}?`)) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/clients/${clientId}/documents/${docType}`,
        {
          headers: { 'Authorization': `Bearer ${getToken()}` },
        }
      );
      
      toast.success(`${label} deleted`);
      onDeleteSuccess?.(docType);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      await axios.put(
        `${API_URL}/api/clients/${clientId}/documents/${docType}/verify`,
        {},
        {
          headers: { 'Authorization': `Bearer ${getToken()}` },
        }
      );
      
      toast.success(`${label} verified`);
      onVerifySuccess?.(docType);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify document');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className={`p-4 border rounded-lg ${STATUS_COLORS[status]} transition-colors`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileText size={16} />
          <span className="font-medium text-sm">{label}</span>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[status]}`}>
          {STATUS_LABELS[status]}
        </span>
      </div>
      
      {status === 'not_uploaded' ? (
        <div className="mt-2">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            disabled={disabled || uploading}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || uploading || !clientId}
            className="w-full"
          >
            {uploading ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={14} className="mr-2" />
                Upload Document
              </>
            )}
          </Button>
          {!clientId && (
            <p className="text-xs text-slate-400 mt-1 text-center">Save client first to upload</p>
          )}
        </div>
      ) : (
        <div className="mt-2 space-y-2">
          <p className="text-xs truncate" title={fileName}>
            📎 {fileName || 'Document uploaded'}
          </p>
          <div className="flex gap-2">
            {status === 'uploaded' && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleVerify}
                disabled={verifying}
                className="flex-1 text-green-600 border-green-300 hover:bg-green-50"
              >
                {verifying ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    <CheckCircle size={14} className="mr-1" />
                    Verify
                  </>
                )}
              </Button>
            )}
            {status === 'verified' && (
              <div className="flex-1 flex items-center justify-center gap-1 text-green-600 text-sm">
                <Check size={14} />
                Verified
              </div>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleDelete}
              className="text-red-500 border-red-200 hover:bg-red-50"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
